import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import ChartOfAccountForm from './components/ChartOfAccountForm';
import ViewChartOfAccount from './components/ViewChartOfAccount';
import {
  createChartOfAccount,
  deleteChartOfAccount,
  getChartOfAccounts,
  updateChartOfAccount
} from './api/chartOfAccountApi';

const ChartOfAccount = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await getChartOfAccounts();
      setAccounts(Array.isArray(response) ? response : response.data || []);
    } catch (error) {
      console.error('Error loading chart of accounts:', error);
      showAlert('danger', 'Failed to load chart of accounts.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return accounts.filter(item => {
      const matchesSearch = !query ||
        (item.accountCode && item.accountCode.toLowerCase().includes(query)) ||
        (item.accountName && item.accountName.toLowerCase().includes(query)) ||
        (item.accountType && item.accountType.toLowerCase().includes(query));
      const matchesType = !filterData.accountType || item.accountType === filterData.accountType;
      return matchesSearch && matchesType;
    });
  }, [accounts, filterData, searchQuery]);

  const columns = [
    { key: 'accountCode', label: 'Code', render: value => value || '-' },
    { key: 'accountName', label: 'Name', render: value => value || '-' },
    { key: 'accountType', label: 'Type', render: value => value || '-' },
    { key: 'owner', label: 'Owner', render: value => value || 'Finance' },
    { key: 'cdate', label: 'Create Date', render: value => value ? new Date(value).toLocaleString() : '-' }
  ];

  const handleFormSubmit = async (formData) => {
    const payload = {
      accountCode: formData.accountCode,
      accountName: formData.accountName,
      accountType: formData.accountType || null,
      description: formData.description || null
    };

    try {
      if (selectedAccount) {
        await updateChartOfAccount(selectedAccount.id, payload);
        showAlert('success', 'Chart of account updated successfully!');
      } else {
        await createChartOfAccount(payload);
        showAlert('success', 'Chart of account created successfully!');
      }
      await loadAccounts();
      setIsModalOpen(false);
      setSelectedAccount(null);
    } catch (error) {
      console.error('Error saving chart of account:', error);
      const message = error?.response?.data || 'Failed to save chart of account. Please check your input.';
      showAlert('danger', typeof message === 'string' ? message : 'Failed to save chart of account.');
    }
  };

  const accountTypeOptions = [...new Set(accounts.map(item => item.accountType).filter(Boolean))]
    .sort()
    .map(type => ({ label: type, value: type }));

  return (
    <div className="container-xl">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Chart of Account Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <button className="btn btn-primary" onClick={() => { setSelectedAccount(null); setIsModalOpen(true); }}>
            <Plus className="icon" size={18} />
            Create
          </button>
        </div>
      </div>

      <ChartOfAccountForm isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedAccount(null); }} onSubmit={handleFormSubmit} initialData={selectedAccount} />
      <ViewChartOfAccount isOpen={isViewModalOpen} onClose={() => { setIsViewModalOpen(false); setSelectedAccount(null); }} data={selectedAccount} />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setItemToDelete(null); }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteChartOfAccount(itemToDelete.id);
            showAlert('success', 'Chart of account deactivated successfully!');
            await loadAccounts();
          } catch (error) {
            console.error('Error deleting chart of account:', error);
            showAlert('danger', 'Failed to deactivate chart of account.');
          } finally {
            setIsLoading(false);
            setShowDeleteModal(false);
            setItemToDelete(null);
          }
        }}
        title="Deactivate Chart of Account"
        message={'Are you sure you want to deactivate "' + (itemToDelete?.accountName || 'this account') + '"?'}
        confirmText="Deactivate"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-chart-of-account" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-chart-of-account" role="tabpanel">
                    <GenericView
                      data={filteredData}
                      columns={columns}
                      onEdit={(row) => { setSelectedAccount(row); setIsModalOpen(true); }}
                      onDelete={(row) => { setItemToDelete(row); setShowDeleteModal(true); }}
                      onView={(row) => { setSelectedAccount(row); setIsViewModalOpen(true); }}
                      isLoading={isLoading}
                      filterComponent={<FilterDropdown filterData={filterData} onFilterChange={(name, value) => setFilterData(prev => ({ ...prev, [name]: value }))} onReset={() => setFilterData({})} onApply={() => {}} showDateFilter={false} filterFields={[{ name: 'accountType', label: 'Account Type', type: 'select', options: accountTypeOptions }]} />}
                      searchComponent={<SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search account code, name, or type..." />}
                      actionButtons={<ActionButtons onExportExcel={() => showAlert('info', 'Export to Excel functionality coming soon')} onExportPDF={() => showAlert('info', 'Export to PDF functionality coming soon')} />}
                      emptyStateMessage="No chart of account records found"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartOfAccount;
